/**
 * The person's Pinned chats (glossary: Pinned chat, ADR 0035): one board across
 * every Account, kept on the CRM server and shared by Workspace's Pinned and
 * each Account's Chats page. Pins and unpins show at once and go to the server
 * one chat at a time, so a phone and a computer never overwrite each other; a
 * server answer only replaces the board when no newer change started since.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { addPins, fetchPins, removePin, savePins, type PinRow } from './api';
import { createDebouncedSaver } from './saver';

export interface PinState {
  pins: PinRow[];
  loaded: boolean;
}

export interface PinApi {
  fetch: () => Promise<PinRow[]>;
  add: (pins: PinRow[]) => Promise<{ pins: PinRow[]; full: boolean }>;
  remove: (accountId: string, fanId: string) => Promise<PinRow[]>;
  save: (pins: PinRow[]) => Promise<PinRow[]>;
}

export type PinResult = 'pinned' | 'full' | 'failed';

const REFRESH_MS = 30_000;
const EMPTY: PinState = { pins: [], loaded: false };

const same = (a: { account_id: string; fan_id: string }, b: { account_id: string; fan_id: string }) =>
  a.account_id === b.account_id && a.fan_id === b.fan_id;

export function createPinStore(api: PinApi, opts: { saveDelayMs?: number; now?: () => number } = {}) {
  const now = opts.now ?? Date.now;
  let state: PinState = EMPTY;
  let person: string | null = null;
  let loading: Promise<void> | null = null;
  let lastLoad = 0;
  /** Bumped by every change; an answer started before a later change is stale. */
  let seq = 0;
  const listeners = new Set<() => void>();

  const set = (next: PinState) => {
    state = next;
    listeners.forEach((fn) => fn());
  };
  const setPins = (pins: PinRow[]) => set({ pins, loaded: true });

  async function load(): Promise<void> {
    if (loading) return loading;
    const mine = seq;
    const who = person;
    loading = api
      .fetch()
      .then((pins) => {
        if (who !== person) return;
        lastLoad = now();
        if (mine === seq) setPins(pins);
        else if (!state.loaded) set({ ...state, loaded: true });
      })
      .catch(() => {
        if (who === person && !state.loaded) set({ ...state, loaded: true });
      })
      .finally(() => {
        loading = null;
      });
    return loading;
  }

  /** A new order (Workspace's drag): kept for chats still pinned on the server, plus any pinned elsewhere meanwhile. */
  const saver = createDebouncedSaver<PinRow[]>(async (order) => {
    const mine = seq;
    const server = await api.fetch();
    const kept = order.filter((p) => server.some((s) => same(s, p)));
    const merged = [...kept, ...server.filter((s) => !order.some((p) => same(p, s)))];
    const saved = await api.save(merged);
    if (mine === seq) setPins(saved);
  }, opts.saveDelayMs ?? 400);

  return {
    get: () => state,
    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    /** Pins belong to one signed-in person: another person starts from nothing. */
    forPerson(id: string) {
      if (id === person) return;
      person = id;
      seq += 1;
      loading = null;
      lastLoad = 0;
      saver.cancel();
      set(EMPTY);
    },
    load,
    /** Reload if the board is older than 30 s (back on the tab: maybe pinned on another device). */
    refresh(): Promise<void> {
      if (!state.loaded) return load();
      return now() - lastLoad >= REFRESH_MS ? load() : Promise.resolve();
    },
    isPinned: (accountId: string, fanId: string) => state.pins.some((p) => p.account_id === accountId && p.fan_id === fanId),
    async add(row: PinRow): Promise<PinResult> {
      if (state.pins.some((p) => same(p, row))) return 'pinned';
      const mine = ++seq;
      setPins([...state.pins, row]);
      const takeBack = () => setPins(state.pins.filter((p) => !same(p, row)));
      try {
        const res = await api.add([row]);
        const kept = res.pins.some((p) => same(p, row));
        if (mine === seq) setPins(res.pins);
        else if (!kept) takeBack();
        return kept ? 'pinned' : res.full ? 'full' : 'failed';
      } catch {
        takeBack();
        return 'failed';
      }
    },
    async remove(accountId: string, fanId: string): Promise<boolean> {
      const at = state.pins.findIndex((p) => p.account_id === accountId && p.fan_id === fanId);
      const row = state.pins[at];
      if (!row) return true;
      const mine = ++seq;
      setPins(state.pins.filter((_, i) => i !== at));
      try {
        const pins = await api.remove(accountId, fanId);
        if (mine === seq) setPins(pins);
        return true;
      } catch {
        if (!state.pins.some((p) => same(p, row))) {
          const pins = [...state.pins];
          pins.splice(Math.min(at, pins.length), 0, row);
          setPins(pins);
        }
        return false;
      }
    },
    reorder(order: PinRow[]) {
      seq += 1;
      setPins(order);
      saver.queue(order);
    },
    flush: () => saver.flush(),
    /** One-time upload of pins kept in a browser before they moved to the server. */
    async upload(rows: PinRow[]): Promise<{ ok: boolean; full: boolean }> {
      if (!rows.length) return { ok: true, full: false };
      const mine = ++seq;
      try {
        const res = await api.add(rows);
        if (mine === seq) setPins(res.pins);
        return { ok: true, full: res.full };
      } catch {
        return { ok: false, full: false };
      }
    },
  };
}

export type PinStore = ReturnType<typeof createPinStore>;

/** The page's one board, on the CRM server. */
export const pinStore = createPinStore({ fetch: fetchPins, add: addPins, remove: removePin, save: savePins });

/**
 * Loads the signed-in person's pins on first use, and again (at most every
 * 30 s) when the tab comes back into view.
 */
export function useLoadPins(personId: string | null | undefined, enabled = true): void {
  useEffect(() => {
    if (!enabled || !personId) return;
    pinStore.forPerson(personId);
    void pinStore.load();
    const onShow = () => {
      if (document.visibilityState === 'visible') void pinStore.refresh();
    };
    document.addEventListener('visibilitychange', onShow);
    return () => document.removeEventListener('visibilitychange', onShow);
  }, [personId, enabled]);
}

/** The signed-in person's pins (loaded as useLoadPins says). */
export function usePins(personId: string | null | undefined, enabled = true): PinState {
  useLoadPins(personId, enabled);
  return useSyncExternalStore(pinStore.subscribe, pinStore.get);
}
